import { useMemo, useState } from 'react';

import { ChevronDown, ChevronRight, ListChecks, Play } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { BranchCommitPicker } from '@/components/BranchCommitPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useConfigBranches } from '@/hooks/useConfigRepository';
import {
  useCreateScenarioRun,
  useScenarioAssignments,
  useScenarioRuns,
} from '@/hooks/useScenarioRuns';
import { channelKey, channelLabel } from '@/lib/api/scenario-runs.api';
import { extractErrorMessage } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { formatDateTime } from '@/utils/format';

import {
  RunCounts,
  ScenarioRunStatusBadge,
  targetLabel,
} from '../agent-improvement/scenario-runs/badges';

import type { ScenarioChannel, ScenarioRunTargetKind } from '@/lib/api/scenario-runs.api';

const RECENT_RUNS = 5;

interface RunScenariosDialogProps {
  configurationId: string;
  channels: ScenarioChannel[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RunScenariosDialog = ({
  configurationId,
  channels,
  open,
  onOpenChange,
}: RunScenariosDialogProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');
  const createRun = useCreateScenarioRun();

  const [selected, setSelected] = useState<Set<string>>(() => new Set(channels.map(channelKey)));
  const [targetKind, setTargetKind] = useState<ScenarioRunTargetKind>('live');
  const [branchId, setBranchId] = useState('');
  const [commitId, setCommitId] = useState('');

  const { data: branches } = useConfigBranches(
    isAdmin && targetKind === 'branch' ? configurationId : undefined,
  );

  const toggle = (key: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const canRun =
    selected.size > 0 &&
    !createRun.isPending &&
    (targetKind === 'live' ||
      (targetKind === 'branch' && branchId !== '') ||
      (targetKind === 'commit' && commitId !== ''));

  const handleRun = async () => {
    try {
      const run = await createRun.mutateAsync({
        configurationId,
        target:
          targetKind === 'branch'
            ? { kind: 'branch', branchId }
            : targetKind === 'commit'
              ? { kind: 'commit', commitId }
              : { kind: 'live' },
        channels: channels.filter((ch) => selected.has(channelKey(ch))),
      });
      onOpenChange(false);
      navigate(`/agent-improvement/scenario-runs/${run.id}`);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to start scenario run',
        description: extractErrorMessage(err),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run scenarios</DialogTitle>
          <DialogDescription>
            Runs every enabled scenario assigned to this configuration on the selected channels.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Channels</p>
            {channels.map((ch) => {
              const key = channelKey(ch);
              return (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={selected.has(key)}
                    onCheckedChange={(checked) => toggle(key, checked === true)}
                  />
                  {channelLabel(ch)}
                </label>
              );
            })}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Target</p>
            <Select
              value={targetKind}
              onValueChange={(next) => setTargetKind(next as ScenarioRunTargetKind)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Live configuration</SelectItem>
                <SelectItem value="branch">Branch head</SelectItem>
                <SelectItem value="commit">Specific commit</SelectItem>
              </SelectContent>
            </Select>

            {targetKind === 'branch' &&
              (isAdmin ? (
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches?.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name} ({b.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-muted-foreground text-xs">
                  Branch targets require admin access; pick a commit instead.
                </p>
              ))}

            {targetKind === 'commit' && (
              <BranchCommitPicker
                configurationId={configurationId}
                value={commitId}
                onChange={setCommitId}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canRun} onClick={() => void handleRun()}>
            <Play className="mr-1.5 h-4 w-4" />
            {createRun.isPending ? 'Starting...' : 'Run'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ScenariosSection = ({ configurationId }: { configurationId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: assignments, isLoading: assignmentsLoading } =
    useScenarioAssignments(configurationId);
  const { data: runs, isLoading: runsLoading } = useScenarioRuns(configurationId);

  const channels = useMemo(() => {
    const seen = new Map<string, ScenarioChannel>();
    for (const a of assignments ?? []) {
      if (!a.enabled) continue;
      const key = channelKey(a);
      if (!seen.has(key)) seen.set(key, { transport: a.transport, modality: a.modality });
    }
    return [...seen.values()];
  }, [assignments]);

  const recentRuns = (runs ?? []).slice(0, RECENT_RUNS);

  return (
    <section className="rounded-lg border dark:border-gray-700">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {expanded ? (
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        ) : (
          <ChevronRight className="text-muted-foreground h-4 w-4" />
        )}
        <ListChecks className="h-4 w-4" />
        <span className="text-sm font-medium">Scenarios</span>
        <span className="text-muted-foreground text-xs">
          {assignments ? `${assignments.length} assigned` : 'Test scenarios for this configuration'}
        </span>
      </button>

      {expanded && (
        <div className="space-y-4 border-t px-4 py-3 dark:border-gray-700">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm">Assigned scenarios</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/agent-improvement/scenarios">Manage</Link>
                  </Button>
                  <Button
                    size="sm"
                    disabled={channels.length === 0}
                    onClick={() => setDialogOpen(true)}
                  >
                    <Play className="mr-1.5 h-4 w-4" />
                    Run scenarios
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {assignmentsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-3/4" />
                </div>
              ) : !assignments?.length ? (
                <p className="text-muted-foreground text-sm">
                  No scenarios assigned. Assign one from the Scenarios library.
                </p>
              ) : (
                <div className="space-y-1">
                  {assignments.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-2 border-b py-1.5 text-sm last:border-0"
                    >
                      <Link
                        to={`/agent-improvement/scenarios/${a.scenario_id}`}
                        className="text-primary hover:underline"
                      >
                        {a.scenario.name}
                      </Link>
                      <span className="text-muted-foreground text-xs">{channelLabel(a)}</span>
                      {a.required && (
                        <Badge variant="secondary" className="text-[10px]">
                          required
                        </Badge>
                      )}
                      {!a.enabled && (
                        <Badge variant="outline" className="text-[10px]">
                          disabled
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm">Recent runs</CardTitle>
                <Button size="sm" variant="ghost" asChild>
                  <Link to="/agent-improvement/scenario-runs">All runs</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <Skeleton className="h-6 w-full" />
              ) : recentRuns.length === 0 ? (
                <p className="text-muted-foreground text-sm">No runs yet.</p>
              ) : (
                <div className="space-y-1">
                  {recentRuns.map((run) => (
                    <div
                      key={run.id}
                      className="flex flex-wrap items-center gap-2 border-b py-1.5 text-sm last:border-0"
                    >
                      <Link
                        to={`/agent-improvement/scenario-runs/${run.id}`}
                        className="text-primary font-mono text-xs hover:underline"
                      >
                        {run.id.slice(0, 8)}
                      </Link>
                      <ScenarioRunStatusBadge status={run.status} />
                      <span className="text-muted-foreground font-mono text-xs">
                        {targetLabel(run)}
                      </span>
                      <RunCounts run={run} />
                      <span className="text-muted-foreground ml-auto text-xs">
                        {formatDateTime(run.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {dialogOpen && (
        <RunScenariosDialog
          configurationId={configurationId}
          channels={channels}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </section>
  );
};
